/**
 * Card primitives shared by every game engine.
 *
 * Cards travel as compact string ids so state snapshots and network frames stay small:
 *   "AS"   – Ace of spades (single-deck games)
 *   "TH:2" – Ten of hearts from deck #2 (multi-deck games such as Marriage)
 *   "JK:0" – printed joker #0 (only when a game's config enables printed jokers)
 */

export type Suit = 'S' | 'H' | 'D' | 'C';
export const SUITS: readonly Suit[] = ['S', 'H', 'D', 'C'];

/** Numeric rank: 2..10, J=11, Q=12, K=13, A=14. */
export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
export const RANKS: readonly Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

export type CardId = string;

export interface Card {
  readonly id: CardId;
  readonly rank: Rank;
  readonly suit: Suit;
  /** Deck index for multi-deck games, 0 otherwise. */
  readonly deck: number;
  readonly joker: boolean;
}

const RANK_CHARS: Record<Rank, string> = {
  2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: 'T', 11: 'J', 12: 'Q', 13: 'K', 14: 'A',
};
const CHAR_RANKS: Record<string, Rank> = Object.fromEntries(
  Object.entries(RANK_CHARS).map(([r, c]) => [c, Number(r) as Rank]),
) as Record<string, Rank>;

export const JOKER_PREFIX = 'JK';

export function makeCardId(rank: Rank, suit: Suit, deck?: number): CardId {
  const base = `${RANK_CHARS[rank]}${suit}`;
  return deck === undefined ? base : `${base}:${deck}`;
}

const cache = new Map<CardId, Card>();

/** Parses a card id. Throws on malformed input — callers validate untrusted ids with {@link isCardId}. */
export function parseCard(id: CardId): Card {
  const hit = cache.get(id);
  if (hit) return hit;
  const [face, deckPart] = id.split(':');
  const deck = deckPart === undefined ? 0 : Number(deckPart);
  if (!face || !Number.isInteger(deck) || deck < 0) throw new Error(`Invalid card id: ${id}`);
  let card: Card;
  if (face === JOKER_PREFIX) {
    // Printed jokers have no natural rank/suit; give them sentinel values never used for matching.
    card = { id, rank: 2, suit: 'S', deck, joker: true };
  } else {
    const rank = CHAR_RANKS[face[0] ?? ''];
    const suit = face[1] as Suit;
    if (face.length !== 2 || rank === undefined || !SUITS.includes(suit)) throw new Error(`Invalid card id: ${id}`);
    card = { id, rank, suit, deck, joker: false };
  }
  cache.set(id, card);
  return card;
}

export function isCardId(value: unknown): value is CardId {
  if (typeof value !== 'string' || value.length > 8) return false;
  try {
    parseCard(value);
    return true;
  } catch {
    return false;
  }
}

export const rankOf = (id: CardId): Rank => parseCard(id).rank;
export const suitOf = (id: CardId): Suit => parseCard(id).suit;
export const isPrintedJoker = (id: CardId): boolean => parseCard(id).joker;

/** Builds `decks` standard 52-card decks (+ optional printed jokers per deck). */
export function buildDeck(options: { decks?: number; printedJokersPerDeck?: number } = {}): CardId[] {
  const decks = options.decks ?? 1;
  const jokers = options.printedJokersPerDeck ?? 0;
  const multi = decks > 1 || jokers > 0;
  const out: CardId[] = [];
  for (let d = 0; d < decks; d++) {
    for (const suit of SUITS) for (const rank of RANKS) out.push(makeCardId(rank, suit, multi ? d : undefined));
    for (let j = 0; j < jokers; j++) out.push(`${JOKER_PREFIX}:${d * jokers + j}`);
  }
  return out;
}

const SUIT_ORDER: Record<Suit, number> = { S: 0, H: 1, C: 2, D: 3 };

/** Sort for display: by suit (alternating colours), then rank descending. */
export function sortHand(cards: readonly CardId[], suitOrder: Record<Suit, number> = SUIT_ORDER): CardId[] {
  return [...cards].sort((a, b) => {
    const ca = parseCard(a);
    const cb = parseCard(b);
    if (ca.joker !== cb.joker) return ca.joker ? 1 : -1;
    if (ca.suit !== cb.suit) return suitOrder[ca.suit] - suitOrder[cb.suit];
    if (ca.rank !== cb.rank) return cb.rank - ca.rank;
    return ca.deck - cb.deck;
  });
}

/** Same rank+suit, ignoring deck index ("identical" cards in multi-deck games). */
export function sameFace(a: CardId, b: CardId): boolean {
  const ca = parseCard(a);
  const cb = parseCard(b);
  return ca.joker === cb.joker && (ca.joker || (ca.rank === cb.rank && ca.suit === cb.suit));
}

export function isRed(suit: Suit): boolean {
  return suit === 'H' || suit === 'D';
}

export function rankLabel(rank: Rank): string {
  return rank === 10 ? '10' : RANK_CHARS[rank];
}

/** Removes exactly the given card ids from a hand; returns false (and leaves hand untouched) if any is missing. */
export function removeCards(hand: CardId[], cards: readonly CardId[]): boolean {
  const idx: number[] = [];
  const used = new Set<number>();
  for (const c of cards) {
    const i = hand.findIndex((h, k) => h === c && !used.has(k));
    if (i < 0) return false;
    used.add(i);
    idx.push(i);
  }
  idx.sort((a, b) => b - a).forEach((i) => hand.splice(i, 1));
  return true;
}
